import { useCurrentPerson } from 'context/currentPerson';
import { LabsProvider } from 'context/labs';
import { LabsActivationProvider } from 'context/labsActivation';
import { RoutineProvider } from 'context/routine';
import LogRocket from 'logrocket';
import React, { useEffect, lazy, Suspense } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { RouteItem } from 'types';

// ✅ Create wrapper components that lazy load only when needed
const LazyDashboardLayout = (): JSX.Element => {
  const DashboardLayout = lazy(
    () => import('components/layouts/DashboardLayout')
  );
  return (
    <Suspense fallback={<div>Loading dashboard...</div>}>
      <DashboardLayout />
    </Suspense>
  );
};

const LazyQuestionnaireLayout = (): JSX.Element => {
  const QuestionnaireLayout = lazy(
    () => import('components/layouts/QuestionnaireLayout')
  );
  return (
    <Suspense fallback={<div>Loading questionnaire...</div>}>
      <QuestionnaireLayout />
    </Suspense>
  );
};

const LazyRoutineLayout = (): JSX.Element => {
  const RoutineLayout = lazy(() => import('components/layouts/RoutineLayout'));
  return (
    <Suspense fallback={<div>Loading routine...</div>}>
      <RoutineLayout />
    </Suspense>
  );
};

const LazyLabOrderForm = (): JSX.Element => {
  const LabOrderForm = lazy(() => import('components/molecules/LabOrderForm'));
  return (
    <Suspense fallback={<div>Loading form...</div>}>
      <LabOrderForm />
    </Suspense>
  );
};

const LazyCheckout = (): JSX.Element => {
  const Checkout = lazy(() => import('components/pages/Checkout'));
  return (
    <Suspense fallback={<div>Loading checkout...</div>}>
      <Checkout />
    </Suspense>
  );
};

const LazyLabKitActivation = (): JSX.Element => {
  const LabKitActivation = lazy(
    () => import('components/pages/LabKitActivation')
  );
  return (
    <Suspense fallback={<div>Loading activation...</div>}>
      <LabKitActivation />
    </Suspense>
  );
};

const LazyLabsPage = (): JSX.Element => {
  const LabsPage = lazy(() => import('components/pages/Labs'));
  return (
    <Suspense fallback={<div>Loading labs...</div>}>
      <LabsPage print />
    </Suspense>
  );
};

const LazyRoutinesIntroPage = (): JSX.Element => {
  const RoutinesIntroPage = lazy(
    () => import('components/pages/RoutinesIntroPage')
  );
  return (
    <Suspense fallback={<div>Loading routines...</div>}>
      <RoutinesIntroPage />
    </Suspense>
  );
};

const LazySectionDetailPage = (): JSX.Element => {
  const SectionDetailPage = lazy(
    () => import('components/pages/SectionDetailPage')
  );
  return (
    <Suspense fallback={<div>Loading section...</div>}>
      <SectionDetailPage />
    </Suspense>
  );
};

const LazySuccessCheckout = (): JSX.Element => {
  const SuccessCheckout = lazy(
    () => import('components/pages/SuccessCheckout')
  );
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SuccessCheckout />
    </Suspense>
  );
};

const LazyCancelCheckout = (): JSX.Element => {
  const CancelCheckout = lazy(() => import('components/pages/CancelCheckout'));
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <CancelCheckout />
    </Suspense>
  );
};

const LazyLabOrderSuccess = (): JSX.Element => {
  const LabOrderSuccess = lazy(
    () => import('components/pages/LabOrderSuccess')
  );
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LabOrderSuccess />
    </Suspense>
  );
};

const LazyConfirmPhonePage = (): JSX.Element => {
  const ConfirmPhonePage = lazy(
    () => import('components/pages/ConfirmPhonePage')
  );
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ConfirmPhonePage />
    </Suspense>
  );
};

const LazySuccessfulPhoneConfirmation = (): JSX.Element => {
  const SuccessfulPhoneConfirmation = lazy(
    () => import('components/pages/SuccessfulPhoneConfirmation')
  );
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SuccessfulPhoneConfirmation />
    </Suspense>
  );
};

// ✅ Enhanced wrapper components with context providers
const Routine: React.FC = () => (
  <RoutineProvider>
    <LazyRoutineLayout />
  </RoutineProvider>
);

const LabsActivation: React.FC = () => (
  <RoutineProvider>
    <LabsActivationProvider>
      <LazyLabKitActivation />
    </LabsActivationProvider>
  </RoutineProvider>
);

const Labs: React.FC = () => (
  <RoutineProvider>
    <LabsProvider>
      <LazyDashboardLayout />
    </LabsProvider>
  </RoutineProvider>
);

const LabsPrint: React.FC = () => (
  <RoutineProvider>
    <LabsProvider>
      <LazyLabsPage />
    </LabsProvider>
  </RoutineProvider>
);

export const routes: RouteItem[] = [
  {
    Component: LazyQuestionnaireLayout,
    path: '/q',
    exact: false,
    title: 'Questionaires'
  },
  {
    Component: LazyDashboardLayout,
    path: '/plan-detail',
    exact: false,
    title: 'Plan'
  },
  {
    Component: LazyDashboardLayout,
    path: '/dashboard',
    exact: false,
    title: 'Dashboard'
  },
  {
    Component: LazyDashboardLayout,
    path: '/account',
    exact: true,
    title: ''
  },
  {
    Component: Labs,
    path: '/labs',
    exact: true,
    title: 'Labs'
  },
  {
    Component: LabsPrint,
    path: '/labs-print',
    exact: true,
    title: 'Labs'
  },
  {
    Component: LazyLabOrderForm,
    path: '/order',
    exact: false,
    title: 'Order'
  },
  {
    Component: LazyLabOrderSuccess,
    path: '/success-order',
    exact: false,
    title: 'Succesful Order'
  },
  {
    Component: LabsActivation,
    path: '/activate/:kitId',
    exact: true,
    title: ''
  },
  {
    Component: LazyDashboardLayout,
    path: '/order-labs',
    exact: false,
    title: 'Labs'
  },
  {
    Component: LazyDashboardLayout,
    path: '/labs/:diagnosticReportId/:biomarkerSlug',
    exact: true,
    title: 'Biomarker Detail Page'
  },
  {
    Component: LazySectionDetailPage,
    path: '/detail/:sectionSlug',
    exact: true,
    title: 'Section Detail'
  },
  {
    Component: LazyCheckout,
    path: '/checkout',
    exact: false,
    title: 'Checkout Subscription'
  },
  {
    Component: LazySuccessCheckout,
    path: '/success-checkout',
    exact: false,
    title: 'Succesful Checkout'
  },
  {
    Component: LazyCancelCheckout,
    path: '/cancel-checkout',
    exact: false,
    title: 'Cancel Checkout'
  },
  {
    Component: Routine,
    path: '/routine',
    exact: false,
    title: ''
  },
  {
    Component: LazyRoutinesIntroPage,
    path: '/routine-intro',
    exact: true,
    title: ''
  },
  {
    Component: LazyConfirmPhonePage,
    path: '/confirm-phone',
    exact: true,
    title: ''
  },
  {
    Component: LazySuccessfulPhoneConfirmation,
    path: '/confirm-phone-success',
    exact: true,
    title: ''
  }
];

const PortalRouter: React.FC = () => {
  const location = useLocation();
  const person = useCurrentPerson();

  useEffect(() => {
    if (person.id) {
      LogRocket.identify(person.id);
    }
  }, [person.id]);

  return (
    <Switch location={location}>
      {routes.map(
        ({ Component, exact, hide, path }) =>
          !hide && (
            <Route
              component={Component}
              exact={exact}
              key={path}
              path={path}
            ></Route>
          )
      )}
    </Switch>
  );
};

export default PortalRouter;
